import React, { useState, useEffect, useRef } from 'react';
import api from './api';
import UserCard from './UserCard';

const UserSearch = ({ onViewProfile, currentUserId }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [followingSet, setFollowingSet] = useState(new Set());
  const [tagsMap, setTagsMap] = useState({});
  const debounceRef = useRef(null);

  // Fetch following list once on mount
  useEffect(() => {
    api.get('/follows/following')
      .then(res => {
        setFollowingSet(new Set(res.data.map(u => u.id)));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!query.trim()) {
      setResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get(`/auth/users/search?q=${encodeURIComponent(query.trim())}`);
        setResults(res.data);

        // Batch fetch tags
        const ids = res.data.map(u => u.id);
        if (ids.length > 0) {
          const tagsRes = await api.get(`/tags/users?ids=${ids.join(',')}`);
          setTagsMap(tagsRes.data);
        }
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const handleToggleFollow = async (userId) => {
    const isFollowing = followingSet.has(userId);
    try {
      if (isFollowing) {
        await api.delete(`/follows/${userId}`);
        setFollowingSet(prev => { const next = new Set(prev); next.delete(userId); return next; });
      } else {
        await api.post(`/follows/${userId}`);
        setFollowingSet(prev => new Set(prev).add(userId));
      }
    } catch (err) {
      console.error('Toggle follow error:', err);
    }
  };

  return (
    <div>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search runners by name..."
        style={{
          width: '100%',
          padding: '0.75rem 1rem',
          background: '#16161D',
          border: '1px solid rgba(255, 138, 61, 0.2)',
          borderRadius: '10px',
          color: '#FFFFFF',
          fontSize: '0.95rem',
          outline: 'none',
          marginBottom: '1rem',
          boxSizing: 'border-box',
        }}
      />
      {loading && (
        <p style={{ color: '#8b9cb3', fontSize: '0.85rem', textAlign: 'center' }}>Searching...</p>
      )}
      {!loading && results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {results.map(user => (
            <UserCard
              key={user.id}
              user={user}
              isFollowing={followingSet.has(user.id)}
              onToggleFollow={handleToggleFollow}
              onViewProfile={onViewProfile}
              tags={tagsMap[user.id] || []}
              currentUserId={currentUserId}
            />
          ))}
        </div>
      )}
      {!loading && query.trim() && results.length === 0 && (
        <p style={{ color: '#8b9cb3', fontSize: '0.85rem', textAlign: 'center' }}>No runners found</p>
      )}
    </div>
  );
};

export default UserSearch;
